import React, { useState, useEffect } from 'react';
import { getStudent, approveStudent, rejectStudent, uploadStudentPhoto } from '../services/api';
import { useLanguage } from '../LanguageContext';

function StudentDetailModal({ studentId, onClose, onUpdate }) {
  const { t } = useLanguage();
  const [student, setStudent] = useState(null);
  const [comments, setComments] = useState('');
  const [uploading, setUploading] = useState(false);
  const username = localStorage.getItem('username');
  const userRole = localStorage.getItem('role');

  useEffect(() => {
    if (studentId) {
      getStudent(studentId).then(res => {
        setStudent(res.data);
        setComments(res.data.comments || '');
      });
    }
  }, [studentId]);

  const handleApprove = async () => {
    await approveStudent(studentId, username, comments);
    onUpdate();
    onClose();
  };

  const handleReject = async () => {
    await rejectStudent(studentId, username, comments);
    onUpdate();
    onClose();
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 1024 * 1024) {
      alert('File size must be less than 1MB');
      return;
    }
    
    setUploading(true);
    try {
      await uploadStudentPhoto(studentId, file);
      const res = await getStudent(studentId);
      setStudent(res.data);
    } catch (error) {
      alert('Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  if (!student) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t('studentDetails')}</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          <div className="detail-grid">
            <div className="photo-section">
              {student.photoBase64 ? (
                <img 
                  src={`data:image/jpeg;base64,${student.photoBase64}`}
                  alt={student.studentName}
                  className="student-photo"
                />
              ) : (
                <div className="no-photo">
                  <span>No Photo</span>
                </div>
              )}
              <label className="btn btn-secondary" style={{marginTop: '10px', cursor: 'pointer', display: 'block', textAlign: 'center'}}>
                {uploading ? 'Uploading...' : 'Upload Photo'}
                <input 
                  type="file" 
                  accept="image/*" 
                  onChange={handlePhotoUpload}
                  disabled={uploading}
                  style={{display: 'none'}}
                />
              </label>
            </div>

            <div className="info-section">
              <div className="info-row">
                <label>Application No.:</label>
                <span style={{fontWeight: 'bold', color: '#2d5f3f', fontSize: '18px'}}>{student.applicationNumber}</span>
              </div>
              <div className="info-row">
                <label>{t('studentName')}:</label>
                <span>{student.studentName}</span>
              </div>
              <div className="info-row">
                <label>{t('fatherName')}:</label>
                <span>{student.fatherName}</span>
              </div>
              <div className="info-row">
                <label>{t('dateOfBirth')}:</label>
                <span>{student.dateOfBirth}</span>
              </div>
              <div className="info-row">
                <label>{t('gender')}:</label>
                <span>{student.gender}</span>
              </div>
              <div className="info-row">
                <label>{t('nationality')}:</label>
                <span>{student.nationality}</span>
              </div>
              <div className="info-row">
                <label>{t('contactNumber')}:</label>
                <span>{student.contactCountryCode} {student.contactNumber}</span>
              </div>
              {student.secondaryContactNumber && (
                <div className="info-row">
                  <label>Secondary Contact:</label>
                  <span>{student.secondaryContactCountryCode} {student.secondaryContactNumber}</span>
                </div>
              )}
              <div className="info-row">
                <label>{t('email')}:</label>
                <span>{student.email}</span>
              </div>
              <div className="info-row">
                <label>{t('area')}:</label>
                <span>{student.area} - {student.location}</span>
              </div>
              <div className="info-row">
                <label>{t('addressInKuwait')}:</label>
                <span>{student.addressInKuwait}</span>
              </div>
              <div className="info-row">
                <label>{t('nativeAddress')}:</label>
                <span>{student.nativeAddress}</span>
              </div>
              <div className="info-row">
                <label>{t('civilIdStudent')}:</label>
                <span>{student.civilIdStudent}</span>
              </div>
              <div className="info-row">
                <label>{t('civilIdParent')}:</label>
                <span>{student.civilIdParent}</span>
              </div>
              <div className="info-row">
                <label>{t('transportRequired')}:</label>
                <span style={{fontWeight: 'bold', color: student.transportRequired === 'yes' ? '#2d5f3f' : '#666'}}>
                  {student.transportRequired === 'yes' ? t('yes') : t('no')}
                </span>
              </div>
              {student.transportRequired === 'yes' && student.transportOption && (
                <div className="info-row">
                  <label>{t('transportOption')}:</label>
                  <span>
                    {student.transportOption === 'can_cover' && t('transportCanCover')}
                    {student.transportOption === 'cannot_cover' && t('transportCannotCover')}
                  </span>
                </div>
              )}
              <div className="info-row">
                <label>{t('interestedInDonation')}:</label>
                <span style={{fontWeight: 'bold', color: student.interestedInDonation === 'yes' ? '#2d5f3f' : '#666'}}>
                  {student.interestedInDonation === 'yes' ? t('yes') : t('no')}
                </span>
              </div>
              {student.utmSource && (
                <div className="info-row">
                  <label>Source:</label>
                  <span style={{fontWeight: 'bold', color: '#2d5f3f'}}>{student.utmSource}</span>
                </div>
              )}
              <div className="info-row">
                <label>{t('status')}:</label>
                <span className={`status-badge status-${student.status.toLowerCase()}`}>
                  {t(student.status.toLowerCase())}
                </span>
              </div>
              {student.reviewedBy && (
                <>
                  <div className="info-row">
                    <label>{t('reviewedBy')}:</label>
                    <span>{student.reviewedBy}</span>
                  </div>
                  <div className="info-row">
                    <label>Comments:</label>
                    <span>{student.comments}</span>
                  </div>
                </>
              )}
            </div>
          </div>

          {student.status === 'PENDING' && (userRole === 'ADMIN' || userRole === 'MASTER') && (
            <div className="comments-section">
              <label>{t('addComments')}:</label>
              <textarea 
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                rows="3"
                placeholder={t('addComments')}
              />
              <div className="modal-actions">
                <button className="btn btn-success" onClick={handleApprove}>
                  {t('approve')}
                </button>
                <button className="btn btn-danger" onClick={handleReject}>
                  {t('reject')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default StudentDetailModal;
